import { Component, ElementRef, HostListener, OnDestroy, ViewChild, ViewEncapsulation } from '@angular/core';
import { SeatMapComponent } from 'src/app/components/seat-map/seat-map.component';
import { ActivatedRoute, Router } from '@angular/router';
import { faArrowLeft, faCalendarAlt, faTicket, faCheckCircle, faChair, faTrashAlt, faSearch, faSliders, faTimes, faCircleCheck, faTag, faPlus } from '@fortawesome/pro-light-svg-icons';
import { NgbCalendar, NgbDate, NgbDateParserFormatter, NgbDatepickerNavigateEvent, NgbDateStruct } from '@ng-bootstrap/ng-bootstrap';
import { formatDate, getLocaleFirstDayOfWeek } from '@angular/common';
import { PassesService } from 'src/app/services/passes.service';
import { CartService } from 'src/app/services/cart.service';
import { TicketsEventService } from 'src/app/services/tickets-event.service';
import { Subscription } from 'rxjs';
import { AppSettings } from 'src/app/app-settings';
import {
  AppearanceAnimation,
  DialogInitializer,
  DialogLayoutDisplay,
  DisappearanceAnimation,
} from '@costlydeveloper/ngx-awesome-popup';
import { DialogSelectPaymentComponent } from 'src/app/components/dialog-select-payment/dialog-select-payment.component';
import { DialogPayComponent } from 'src/app/components/dialog-pay/dialog-pay.component';
import { DialogErrorBondsComponent } from 'src/app/components/dialog-error-bonds/dialog-error-bonds.component';
import { MatDialog } from '@angular/material/dialog';
import { DialogGenericComponent } from 'src/app/components/dialog-generic/dialog-generic.component';
import { SingleSeatPassOfferService } from 'src/app/services/single-seat-pass-offer.service';
import { isFixedPricePass } from 'src/app/helpers/fixed-price-pass';

@Component({
  selector: 'app-pass-sale',
  templateUrl: './pass-sale.component.html',
  styleUrls: ['./pass-sale.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class PassSaleComponent implements OnDestroy {
  pass: any = null;
  loading: boolean = true;
  passId: number;

  // Current event being viewed in center panel
  currentEventId: number | null = null;

  // Local selections: Map<eventId, cartLine payload>
  eventSelections: Map<number, any> = new Map();

  // Allowed session IDs per event (null = all sessions allowed)
  allowedSessionsByEvent: Map<number, number[] | null> = new Map();
  private deduplicatedPassItems: any[] = [];

  // Adding to cart state
  submitting: boolean = false;
  passAdded: boolean = false;

  panelCollapsed: boolean = false;
  private collapsedSub: Subscription;

  // Enriched selections for display (event title, ticket name, seat info, etc.)
  selectionDetails: Map<number, any> = new Map();

  private routeSubscription: Subscription;

  // Abono de butaca única (`pass.single_seat`): no hay recorrido evento a
  // evento. Se eligen `passQuantity` butacas en el mapa del abono (una por
  // abono) y el back devuelve el desglose y resuelve la entrada de cada evento.
  passQuantity: number = 1;
  passSeats: { id: number, level: string | null, section: string | null, row: any, seat: any }[] = [];
  // Mismo contenido que `passSeats`, pero con referencia estable: el mapa
  // repinta la selección cada vez que este array cambia.
  passSeatIds: number[] = [];
  passQuote: any = null;
  loadingQuote: boolean = false;
  private quoteSubscription: Subscription;

  faArrowLeft = faArrowLeft;
  faCalendar = faCalendarAlt;
  faTicket = faTicket;
  faCheck = faCheckCircle;
  faChair = faChair;
  faTrash = faTrashAlt;
  faSearch = faSearch;
  faSliders = faSliders;
  faTimes = faTimes;
  faCircleCheck = faCircleCheck;
  faTag = faTag;
  faPlus = faPlus;
  urlImages = AppSettings.API_EVENTS_ASSETS;

  // Filters
  filtersOpen: boolean = false;
  searchText: string = '';
  stringDate: string = '';
  openDatePicker: boolean = false;
  fromDate!: any;
  toDate!: any;
  hoveredDate!: any;
  currentMonth: number;
  minDate: NgbDateStruct;
  model: NgbDateStruct;
  @ViewChild('fechaDropdown') fechaDropdown!: ElementRef;
  @ViewChild(SeatMapComponent) seatMap?: SeatMapComponent;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private passesService: PassesService,
    private cartService: CartService,
    private ticketsEventService: TicketsEventService,
    private calendar: NgbCalendar,
    public formatter: NgbDateParserFormatter,
    private dialog: MatDialog,
    private sameSeatOffer: SingleSeatPassOfferService,
  ) {
    this.model = this.calendar.getToday();
    this.minDate = { day: 1, month: 0, year: 2020 };
    this.currentMonth = this.model.month;
  }

  ngOnInit(): void {
    this.collapsedSub = this.cartService.cartCollapsed$.subscribe(v => this.panelCollapsed = v);
    this.passQuantity = Math.max(1, +this.route.snapshot.queryParams['quantity'] || 1);
    this.routeSubscription = this.route.params.subscribe(params => {
      this.passId = +params['idPass'];
      const idEvent = params['idEvent'];
      this.currentEventId = idEvent ? +idEvent : null;

      if (!this.pass || this.pass.id !== this.passId) {
        this.loadPass(this.passId);
      }
    });
  }

  loadPass(id: number) {
    this.loading = true;
    this.passesService.getPassDetail(id).subscribe({
      next: (pass) => {
        this.pass = pass;
        this.buildDeduplicatedItems();
        this.loading = false;
        if (this.isSingleSeat()) {
          this.openSingleSeatMap();
          return;
        }
        if (!this.currentEventId) {
          const items = this.getPassItems();
          if (items.length > 0) {
            const firstEvent = this.getEvent(items[0]);
            this.router.navigate(['/pass-sale', this.passId, 'event', firstEvent.id], { replaceUrl: true });
          }
        }
      },
      error: () => {
        this.loading = false;
      }
    });
  }

  private buildDeduplicatedItems() {
    const raw = this.pass?.pass_items || this.pass?.passItems || [];
    const byEvent = new Map<number, any[]>();
    for (const item of raw) {
      const event = item.event || item;
      const eid: number = item.event_id ?? event.id;
      if (!byEvent.has(eid)) byEvent.set(eid, []);
      byEvent.get(eid)!.push(item);
    }
    this.allowedSessionsByEvent = new Map();
    byEvent.forEach((items, eventId) => {
      const hasNull = items.some((i: any) => !i.session_id);
      this.allowedSessionsByEvent.set(eventId, hasNull ? null : items.map((i: any) => i.session_id));
    });
    this.deduplicatedPassItems = Array.from(byEvent.values()).map(items => items[0]);
  }

  getPassItems(): any[] {
    return this.deduplicatedPassItems.length ? this.deduplicatedPassItems : (this.pass?.pass_items || this.pass?.passItems || []);
  }

  get currentAllowedSessionIds(): number[] | null {
    if (this.currentEventId == null) return null;
    return this.allowedSessionsByEvent.get(this.currentEventId) ?? null;
  }

  getEvent(item: any): any {
    return item.event || item;
  }

  isFlexible(): boolean {
    const minEvents = this.pass?.min_events;
    const total = this.getPassItems().length;
    return minEvents != null && minEvents < total;
  }

  getMinEvents(): number {
    return this.pass?.min_events || this.getPassItems().length;
  }

  getCompletedCount(): number {
    return this.eventSelections.size;
  }

  isEventCompleted(eventId: number): boolean {
    return this.eventSelections.has(eventId);
  }

  isCurrentEvent(eventId: number): boolean {
    return this.currentEventId === eventId;
  }

  hasMap(item: any): boolean {
    const event = this.getEvent(item);
    return !!event.use_map;
  }

  selectEvent(item: any) {
    // En un abono de butaca única los eventos son solo informativos
    if (this.isSingleSeat()) return;
    const event = this.getEvent(item);
    this.router.navigate(['/pass-sale', this.passId, 'event', event.id]);
  }

  /** Called by seat-map passSelection output */
  onEventSelection(linePayload: any) {
    const eventId = linePayload.id_event;
    const display = linePayload._display || {};
    const firstEventChosen = !Array.from(this.eventSelections.keys()).some(id => id !== eventId);

    // Store selection with pass discount id (remove _display from stored payload)
    const { _display, ...payload } = linePayload;
    this.eventSelections.set(eventId, {
      ...payload,
      id_discount: this.pass.id
    });

    this.selectionDetails.set(eventId, {
      eventTitle: display.eventTitle || 'Evento',
      eventImage: display.eventImage || null,
      ticketName: display.ticketName || 'Entrada',
      ticketPrice: display.ticketPrice ?? null,
      sessionDate: display.sessionDate || null,
      seatLabel: display.seatLabel || null,
      seatSection: display.seatSection || null,
      seatRow: display.seatRow || null,
      seatSeat: display.seatSeat || null,
    });

    // Butaca del primer evento: si está libre en todo el abono se ofrece
    // venderlo entero con ella; si no (o si se rechaza), se sigue evento a evento.
    if (linePayload.id_seat && firstEventChosen && this.sameSeatOffer.canOffer(this.pass, this.getPassItems())) {
      this.sameSeatOffer.offerSameSeat(this.pass, eventId, [linePayload.id_seat]).subscribe((quote) => {
        quote ? this.addSameSeatPass(eventId, [linePayload.id_seat]) : this.goToNextIncompleteEvent(eventId);
      });
      return;
    }

    this.goToNextIncompleteEvent(eventId);
  }

  /** Auto-navigate to next incomplete event, unless all requirements are already met */
  private goToNextIncompleteEvent(eventId: number) {
    if (!this.allEventsCompleted()) {
      const items = this.getPassItems();
      const nextItem = items.find((item: any) => {
        const eid = this.getEvent(item).id;
        return eid !== eventId && !this.eventSelections.has(eid);
      });
      if (nextItem) {
        setTimeout(() => this.selectEvent(nextItem), 500);
      }
    }
  }

  /**
   * Butaca única ofrecida y aceptada: el abono entra en el carrito con la
   * butaca elegida en `eventId` para todos sus eventos. Si entre medias se ha
   * ocupado en alguno, se quita la selección, se recarga el mapa y se avisa;
   * con cualquier otro fallo se sigue evento a evento con la butaca ya elegida.
   */
  private addSameSeatPass(eventId: number, seatIds: number[]) {
    if (this.submitting) return;
    this.submitting = true;

    this.passesService.addSingleSeatPassToCart(this.pass.id, seatIds, eventId).subscribe({
      next: () => {
        this.cartService.getCart().subscribe();
        this.submitting = false;
        this.passAdded = true;
      },
      error: (err) => {
        this.submitting = false;
        const seatsTaken = err?.status === 409 && err?.error?.errors?.code === 'seats_unavailable';
        if (seatsTaken) {
          this.removeSelection(eventId);
          this.seatMap?.getMapSession();
        } else {
          this.goToNextIncompleteEvent(eventId);
        }
        this.dialog.open(DialogGenericComponent, {
          data: {
            title: seatsTaken ? 'Butaca ya ocupada' : 'No se ha podido añadir el abono',
            subtitle: seatsTaken
              ? 'La butaca acaba de ocuparse en uno de los eventos del abono. El mapa se ha actualizado: elige de nuevo.'
              : (err?.error?.errors?.errors?.[0] || err?.error?.message || 'Puedes seguir eligiendo evento a evento.'),
            icon: seatsTaken ? 'info' : 'error',
            buttons: [{ label: 'Cerrar', action: () => this.dialog.closeAll() }],
          },
        });
      }
    });
  }

  /** Remove a selection for an event */
  removeSelection(eventId: number) {
    const selection = this.eventSelections.get(eventId);
    if (selection?.id_seat && eventId === this.currentEventId) {
      this.seatMap?.clearPassSelections([selection.id_seat]);
    }
    this.eventSelections.delete(eventId);
    this.selectionDetails.delete(eventId);
    this.eventSelections = new Map(this.eventSelections);
    this.selectionDetails = new Map(this.selectionDetails);
  }

  /** Remove all selections at once */
  clearAllSelections() {
    if (this.currentEventId) {
      const selection = this.eventSelections.get(this.currentEventId);
      if (selection?.id_seat) {
        this.seatMap?.clearPassSelections([selection.id_seat]);
      }
    }
    this.eventSelections = new Map();
    this.selectionDetails = new Map();
  }

  getSelectionsList(): { eventId: number, detail: any }[] {
    return Array.from(this.selectionDetails.entries()).map(([eventId, detail]) => ({ eventId, detail }));
  }

  trackSelectionByEventId(_index: number, sel: { eventId: number, detail: any }): number {
    return sel.eventId;
  }

  getPreselectedSeatIds(eventId: number): number[] {
    const selection = this.eventSelections.get(eventId);
    if (!selection || !selection.id_seat) return [];
    return [selection.id_seat];
  }

  allEventsCompleted(): boolean {
    return this.eventSelections.size >= this.getMinEvents();
  }

  /** Send all selections as one addPass call */
  addPassToCart() {
    if (this.submitting || !this.allEventsCompleted()) return;
    this.submitting = true;

    const cartLines = Array.from(this.eventSelections.values());

    this.passesService.addPassToCart(this.pass.id, cartLines).subscribe({
      next: () => {
        this.cartService.getCart().subscribe();
        this.submitting = false;
        this.passAdded = true;
      },
      error: () => {
        this.submitting = false;
      }
    });
  }

  // --- Abono de precio fijo por evento y nivel ---

  isFixedPrices(): boolean {
    return isFixedPricePass(this.pass);
  }

  /** Lo que cuesta lo elegido en un evento: precio del abono para su nivel × entradas */
  getSelectionTotal(eventId: number): number {
    const price = this.selectionDetails.get(eventId)?.ticketPrice;
    const quantity = this.eventSelections.get(eventId)?.quantity || 1;
    return price != null ? +price * quantity : 0;
  }

  /** Total de lo elegido; con precio fijo es lo que costará el abono */
  getSelectionsTotal(): number {
    return Array.from(this.selectionDetails.keys()).reduce((sum, eventId) => sum + this.getSelectionTotal(eventId), 0);
  }

  // --- Abono de butaca única ---

  isSingleSeat(): boolean {
    return !!this.pass?.single_seat;
  }

  /**
   * El mapa de taquilla se monta sobre la ruta de un evento (de ahí saca el
   * aforo y el espacio), así que se le da uno del abono: el de sesión más
   * lejana, que es el que seguro sigue vigente. La ocupación y las butacas
   * salen del mapa del abono, no de ese evento.
   */
  private openSingleSeatMap() {
    if (this.currentEventId) return;
    const items = [...this.getPassItems()].sort((a: any, b: any) => this.itemDate(b) - this.itemDate(a));
    if (items.length > 0) {
      this.router.navigate(['/pass-sale', this.passId, 'event', this.getEvent(items[0]).id], {
        replaceUrl: true,
        queryParamsHandling: 'preserve'
      });
    }
  }

  private itemDate(item: any): number {
    return new Date(item.session?.start_date || this.getEvent(item).start_date).getTime();
  }

  /** Eventos del abono por fecha, para la lista informativa */
  getSingleSeatItems(): any[] {
    return [...this.getPassItems()].sort((a: any, b: any) => this.itemDate(a) - this.itemDate(b));
  }

  private setPassSeats(seats: PassSaleComponent['passSeats']) {
    this.passSeats = seats;
    this.passSeatIds = seats.map(s => s.id);
  }

  /** Called by seat-map passSelection output en modo butaca única */
  onPassSeatSelected(selection: any) {
    if (this.passSeats.some(s => s.id === selection.id_seat) || this.passSeats.length >= this.passQuantity) return;
    const display = selection._display || {};
    this.setPassSeats([...this.passSeats, {
      id: selection.id_seat,
      level: display.levelName || null,
      section: display.seatSection || null,
      row: display.seatRow || null,
      seat: display.seatSeat || null,
    }]);
    this.loadPassQuote();
  }

  removePassSeat(seatId: number) {
    this.seatMap?.clearPassSelections([seatId]);
    this.setPassSeats(this.passSeats.filter(s => s.id !== seatId));
    this.loadPassQuote();
  }

  clearPassSeats() {
    this.seatMap?.clearPassSelections(this.passSeatIds);
    this.setPassSeats([]);
    this.loadPassQuote();
  }

  /** Importe de una butaca (su abono entero) según el último desglose */
  getPassSeatTotal(seatId: number): number | null {
    const seat = (this.passQuote?.seats || []).find((s: any) => s.id_seat === seatId);
    return seat ? seat.total : null;
  }

  allPassSeatsSelected(): boolean {
    return this.passSeats.length === this.passQuantity && !!this.passQuote && !this.loadingQuote;
  }

  private loadPassQuote() {
    this.quoteSubscription?.unsubscribe();
    this.passQuote = null;
    if (this.passSeats.length === 0) {
      this.loadingQuote = false;
      return;
    }
    this.loadingQuote = true;
    this.quoteSubscription = this.passesService.quotePassSeats(this.pass.id, this.passSeatIds).subscribe({
      next: (quote) => {
        this.passQuote = quote;
        this.loadingQuote = false;
      },
      error: (err) => {
        this.loadingQuote = false;
        this.onSingleSeatError(err);
      }
    });
  }

  addSingleSeatPassToCart() {
    if (this.submitting || !this.allPassSeatsSelected()) return;
    this.submitting = true;

    this.passesService.addSingleSeatPassToCart(this.pass.id, this.passSeatIds).subscribe({
      next: () => {
        this.cartService.getCart().subscribe();
        this.submitting = false;
        this.passAdded = true;
      },
      error: (err) => {
        this.submitting = false;
        this.onSingleSeatError(err);
      }
    });
  }

  /**
   * Si alguna butaca se ha ocupado en algún evento entre medias, el back no
   * añade nada: se avisa, se descarta la selección y se recarga el mapa.
   */
  private onSingleSeatError(err: any) {
    const seatsTaken = err?.status === 409 && err?.error?.errors?.code === 'seats_unavailable';
    if (seatsTaken) {
      this.setPassSeats([]);
      this.passQuote = null;
      this.seatMap?.getMapSession();
    }
    this.dialog.open(DialogGenericComponent, {
      data: {
        title: seatsTaken ? 'Butaca ya ocupada' : 'No se ha podido añadir el abono',
        subtitle: seatsTaken
          ? 'Alguna de las butacas elegidas acaba de ocuparse en uno de los eventos del abono. El mapa se ha actualizado: elige de nuevo.'
          : (err?.error?.errors?.errors?.[0] || err?.error?.message || 'Inténtalo de nuevo.'),
        icon: seatsTaken ? 'info' : 'error',
        buttons: [{ label: 'Cerrar', action: () => this.dialog.closeAll() }],
      },
    });
  }

  // --- Filtered pass items ---
  getFilteredPassItems(): any[] {
    let items = this.getPassItems();
    if (this.searchText?.trim()) {
      const term = this.searchText.trim().toLowerCase();
      items = items.filter((item: any) => {
        const event = this.getEvent(item);
        return event.title?.toLowerCase().includes(term);
      });
    }
    if (this.fromDate) {
      const from = new Date(this.formatter.format(this.fromDate));
      from.setHours(0, 0, 0, 0);
      const to = this.toDate ? new Date(this.formatter.format(this.toDate)) : new Date(from);
      to.setHours(23, 59, 59, 999);
      items = items.filter((item: any) => {
        const event = this.getEvent(item);
        const start = event.start_date ? new Date(event.start_date) : null;
        if (!start) return true;
        return start >= from && start <= to;
      });
    }
    return items;
  }

  toggleFilters() {
    this.filtersOpen = !this.filtersOpen;
    if (!this.filtersOpen) {
      this.clearFilters();
    }
  }

  onSearchChange(value: string) {
    this.searchText = value;
  }

  clearFilters() {
    this.searchText = '';
    this.fromDate = null;
    this.toDate = null;
    this.stringDate = '';
  }

  hasActiveFilters(): boolean {
    return !!(this.searchText?.trim() || this.fromDate);
  }

  // --- Date picker methods ---
  @HostListener('document:click', ['$event'])
  handleClickOutside(event: MouseEvent): void {
    if (
      this.openDatePicker &&
      this.fechaDropdown &&
      !this.fechaDropdown.nativeElement.contains(event.target)
    ) {
      this.openDatePicker = false;
    }
  }

  changeDate(date: NgbDate) {
    if (!this.fromDate && !this.toDate) {
      this.fromDate = date;
    } else if (this.fromDate && !this.toDate && date.after(this.fromDate)) {
      this.toDate = date;
      this.openDatePicker = false;
    } else {
      this.toDate = null;
      this.fromDate = date;
    }
    const dateString = { min: this.formatter.format(this.fromDate), max: this.formatter.format(this.toDate) };
    let stringDates = this.formatStringDate(dateString.min)!;
    if (dateString.max && dateString.max !== '') {
      stringDates += ' - ' + this.formatStringDate(dateString.max);
    }
    this.stringDate = stringDates;
  }

  dateNavigate($event: NgbDatepickerNavigateEvent) {
    this.currentMonth = $event.next.month;
  }

  formatStringDate(date: any, format: string = 'dd MMM yy') {
    let dateFormat = null;
    if (date) {
      dateFormat = formatDate(new Date(date), format, 'es');
    }
    return dateFormat;
  }

  isHovered(date: NgbDate) {
    return this.fromDate && !this.toDate && this.hoveredDate && date.after(this.fromDate) && date.before(this.hoveredDate);
  }
  isMonday = (date: NgbDate) => this.calendar.getWeekday(date) == this.getFirstDayWeek();
  isSunday = (date: NgbDate) => this.calendar.getWeekday(date) == this.getLastDayWeek();
  isWeekend = (date: NgbDate) => this.calendar.getWeekday(date) >= 6;
  isDisabled = (date: NgbDate) => !(date.equals(this.minDate) || (date.after(this.minDate) && (date.month == this.currentMonth)));

  isFromDate(date: NgbDate) {
    return this.fromDate ? date.equals(this.fromDate) : false;
  }
  isToDate(date: NgbDate) {
    return this.toDate ? date.equals(this.toDate) : false;
  }
  isInside(date: NgbDate) {
    return this.toDate && date.after(this.fromDate) && date.before(this.toDate);
  }
  isRange(date: NgbDate) {
    return (date.equals(this.fromDate) && this.toDate) || (this.toDate && date.equals(this.toDate)) || this.isInside(date);
  }
  getFirstDayWeek() {
    return getLocaleFirstDayOfWeek('es');
  }
  getLastDayWeek() {
    let first = getLocaleFirstDayOfWeek('es');
    let lastCal = first + 6;
    if (lastCal > 7) {
      lastCal = first - (lastCal - 7);
    }
    return lastCal;
  }

  goBack() {
    this.router.navigate(['/passes']);
  }

  pay() {
    this.ticketsEventService.checkCartBonds().subscribe({
      next: (checkBonds) => {
        if (checkBonds.success) {
          this.dialog.open(DialogPayComponent, {
            width: '500px',
            disableClose: false,
          });
        } else {
          this.openErrorBondsDialog(checkBonds.errors.bonds);
        }
      },
      error: (err) => {
        this.openErrorBondsDialog(err?.error?.errors?.bonds);
      }
    });
  }

  private openErrorBondsDialog(bonds: any) {
    const dialogPopup = new DialogInitializer(DialogErrorBondsComponent);
    dialogPopup.setCustomData({ bonds });
    dialogPopup.setConfig({
      width: '600px',
      layoutType: DialogLayoutDisplay.NONE,
      animationIn: AppearanceAnimation.NONE,
      animationOut: DisappearanceAnimation.NONE,
      escapeKeyClose: true,
    });
    dialogPopup.openDialog$().subscribe();
  }

  togglePanel(): void {
    this.panelCollapsed = !this.panelCollapsed;
  }

  ngOnDestroy() {
    this.routeSubscription?.unsubscribe();
    this.collapsedSub?.unsubscribe();
    this.quoteSubscription?.unsubscribe();
  }
}
